import { createAction, createSlice } from '@reduxjs/toolkit';
import { runAccountDetailsMutation, runAccountDetailsQuery, runAccountHistoryTableQuery } from '@/common/account-details/account-details.query';
import { getServerErrorMessage, isUnauthorizedError } from '@/pages/shared/rtk-query-helpers';
import { changeTableRow, setFilter, updateTable } from '@/pages/shared/shared.reducer';
import { getTableData } from '@/pages/shared/shared.reducer';
import { openErrorNotification, openSuccessNotification } from '@/notification';

const ACCOUNT_USER_VALUE_SET = "ACCOUNT_USER_VALUE_SET";
const CLEAR_ACCOUNT_USER_DATA = "CLEAR_ACCOUNT_USER_DATA";
const CLEAR_SYSTEM_DETAILS = "CLEAR_SYSTEM_DETAILS";
const SET_SYSTEM_VALUE = "SET_SYSTEM_VALUE";
const SYSTEM_DETAILS_SUCCESS = "SYSTEM_DETAILS_SUCCESS";
const SET_SYSTEM_DETAIL_LOADING = "SET_SYSTEM_DETAIL_LOADING";

const initialState = {
    sites: [],
    thirdPartySystem: [],
    editThirdPartySystem: [],
    sitesLoading: false,
    accountSystems: [],
    systemDetailLoading: false,
}

// Legacy action type strings are preserved so existing dispatch sites keep working.
export const setAccountUserValue = createAction(ACCOUNT_USER_VALUE_SET, (key, value) => ({
    payload: { key, value }
}));

export const clearData = createAction(CLEAR_ACCOUNT_USER_DATA);

export const clearSystemDetails = createAction(CLEAR_SYSTEM_DETAILS);

export const setSystemDetailsValue = createAction(SET_SYSTEM_VALUE, (key, value) => ({
    payload: { name: key, key, value }
}));

const systemDetailsSuccess = createAction(SYSTEM_DETAILS_SUCCESS, (data) => ({
    payload: { data }
}));

const setSystemDetailLoading = createAction(SET_SYSTEM_DETAIL_LOADING);

const accountDetailsSlice = createSlice({
    name: 'accountDetails',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(setAccountUserValue, (draft, action) => {
                draft[action.payload.key] = action.payload.value;
            })
            .addCase(clearData, () => initialState)
            .addCase(clearSystemDetails, (draft) => ({
                ...initialState,
                accountSystems: draft.accountSystems
            }))
            .addCase(setSystemDetailsValue, (draft, action) => {
                draft.editThirdPartySystem = {
                    ...draft.editThirdPartySystem,
                    [action.payload.name]: action.payload.value
                };
            })
            .addCase(systemDetailsSuccess, (draft, action) => {
                draft.thirdPartySystem = { ...action.payload.data };
                draft.editThirdPartySystem = { ...action.payload.data };
            })
            .addCase(setSystemDetailLoading, (draft, action) => {
                draft.systemDetailLoading = action.payload;
            });
    }
});

const accountDetailsReducer = accountDetailsSlice.reducer;

// Normalize backend SystemsModel ({ system, apiUser: [{ userName, apiPassword }] })
// to the flat shape the details panel form binds to.
export const normalizeSystemDetails = (data) => {
    if (!data) return data;
    const apiUserEntry = Array.isArray(data.apiUser) ? data.apiUser[0] : null;
    return {
        ...data,
        systemName: data.system ?? data.systemName ?? '',
        apiUser: apiUserEntry ? apiUserEntry.userName : (typeof data.apiUser === 'string' ? data.apiUser : ''),
        apiPassword: apiUserEntry ? apiUserEntry.apiPassword : (data.apiPassword ?? '')
    };
}

const getSystemDetailsSuccess = (response) => {
    return systemDetailsSuccess(normalizeSystemDetails(response));
}

export const getHistoryAccount = (accountId) => {
    return (dispatch, store) => {
        dispatch(getTableData('accountHistoryTable', () => runAccountHistoryTableQuery(dispatch, { accountId, searchPhrase: "", page: 1, pageSize: store().userDetails.setting.defaultPageSize || 10 })))
    }
}

export const saveSystem = (model) => {
    return (dispatch) => {
        dispatch(setSystemDetailLoading(true));
        runAccountDetailsMutation(dispatch, 'saveSystem', model)
            .then(saved => {
                openSuccessNotification(`System '${saved.system}' was saved`);
                if (saved.generatedApiKey) {
                    openSuccessNotification(`New API key generated: ${saved.generatedApiKey}`);
                }
                dispatch(getSystemDetailsSuccess(saved));
                const tableId = `accountSystemsTable${saved.accountId}`;
                const isNewRow = !model.systemId || model.systemId <= 0;
                dispatch(changeTableRow(tableId, { ...saved }, 'systemId', false, isNewRow));
                dispatch(setFilter(tableId, 'key', Math.floor(Math.random() * Math.floor(2500))));
                dispatch(updateTable(tableId));
            })
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification("Error saving system, reload page" + (getServerErrorMessage(error) || ''));
                }
            })
            .finally(() => {
                dispatch(setSystemDetailLoading(false));
            });
    }
}

export const generateSystemApiKey = (systemId) => {
    return (dispatch) => {
        return runAccountDetailsMutation(dispatch, 'generateApiKey', systemId)
            .then(data => {
                const key = data?.generatedApiKey;
                if (key) {
                    openSuccessNotification('API key is ready to copy');
                }
                return data;
            })
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification('Error generating API key. Ensure an API user is assigned and active.');
                }
                throw error;
            });
    }
}

export const rotateSystemApiKey = (systemId) => {
    return (dispatch) => {
        return runAccountDetailsMutation(dispatch, 'rotateApiKey', systemId)
            .then(data => {
                const key = data?.generatedApiKey;
                if (key) {
                    openSuccessNotification('API key rotated successfully');
                }
                return data;
            })
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification('Error rotating API key. Ensure an API user is assigned and active.');
                }
                throw error;
            });
    }
}

export const listSystems = (accountId) => {
    return (dispatch) => {
        runAccountDetailsMutation(dispatch, 'listSystems', accountId)
            .then(data => dispatch(setAccountUserValue('systems', data)))
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification("3rd party systems were not loaded");
                }
            })
    }
}

export const getSiteList = (accountId) => {
    return (dispatch) => {
        dispatch(setAccountUserValue('sitesLoading', true));
        runAccountDetailsMutation(dispatch, 'getSiteList', { accountId })
            .then(data => dispatch(setAccountUserValue('sites', data)))
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification("Can't load site list. Please reload page");
                }
            })
            .finally(() => dispatch(setAccountUserValue('sitesLoading', false)));
    }
}

export const canApplyAccountName = (dispatch, accountName, accountId) => {
    return runAccountDetailsQuery(dispatch, 'canApplyAccountName', { accountName, accountId })
        .then(data => { return data })
        .catch(() => {
            return false;
        })
}

export const getSystemDetails = (id) => {
    return (dispatch) => {
        runAccountDetailsQuery(dispatch, 'getSystemDetails', id)
            .then(data => {
                dispatch(getSystemDetailsSuccess(data));
            })
            .catch(error => {
                if (!isUnauthorizedError(error)) {
                    openErrorNotification("Can't load system details. Please reload page")
                }
            })
    }
}

export const deleteSystem = (tableId, item) => {
    return (dispatch) => { 
        runAccountDetailsMutation(dispatch, 'deleteSystem', item.systemId)
        .then(data => {
            openSuccessNotification(`System '${item.system}' was deleted`);
            dispatch(setAccountUserValue('thirdPartySystem', data));
            dispatch(setAccountUserValue('editThirdPartySystem', data));
            dispatch(changeTableRow(tableId, { ...data }, 'siteId'));
            dispatch(setFilter(tableId, 'key', Math.floor(Math.random() * Math.floor(2500))));
            dispatch(updateTable(tableId));
        })
        .catch(error => {
            openErrorNotification('Error deleting system, reload page' + (getServerErrorMessage(error) || '')); 
            console.error('There was an error!', error);
        })
    }
}

export const restoreSystem = (tableId, item) => {
    return (dispatch) => { 
        runAccountDetailsMutation(dispatch, 'restoreSystem', item.systemId)
        .then(data => {
            openSuccessNotification(`System '${item.system}' was restored successfully`);
            dispatch(setAccountUserValue('thirdPartySystem', data));
            dispatch(setAccountUserValue('editThirdPartySystem', data));
            dispatch(changeTableRow(tableId, { ...data }, 'siteId'));
            dispatch(setFilter(tableId, 'key', Math.floor(Math.random() * Math.floor(2500))));
            dispatch(updateTable(tableId));
        })
        .catch(error => {
            openErrorNotification('Error restoring system, reload page' + (getServerErrorMessage(error) || ''));
            console.error('There was an error!', error);
        })
    }
}

export default accountDetailsReducer;